/**
 * Template schema — the CONTRACT between the three template modules (MAR-165).
 *
 * The modules never call each other; they pass this structure:
 *
 *   1. DETECT   (server/services/templates/detect.ts, POST /api/template-drafts/detect)
 *        a .docx  →  TemplateDetection   — a *proposal*, never authoritative
 *   2. MANAGE   (the admin surface)
 *        a reviewed proposal  →  TemplateSchema  — the *approved* artifact, stored
 *   3. EXECUTE  (the fill step)
 *        TemplateSchema + values  →  a filled .docx
 *
 * Keeping the seam a plain data structure is the point: detection can gain passes,
 * execution can change primitive, and neither has to know about the other.
 *
 * ── Two tiers, and why ──────────────────────────────────────────────────────────
 * `inputs` are what the RUN FORM asks a human for. `placeholders` are what gets
 * substituted into the document. They are NOT the same list, and collapsing them
 * would be the first mistake:
 *
 *   one `fecha_suscripcion` date input   → [FECHA_SUSCRIPCION_DIA]   'dieciocho'
 *                                        → [FECHA_SUSCRIPCION_MES]   'marzo'
 *                                        → [FECHA_SUSCRIPCION_ANIO]  'dos mil veintiséis'
 *   one `monto_total` amount input       → [MONTO_TOTAL_CIFRAS]      '6,750.90'
 *                                        → [MONTO_TOTAL_LETRAS]      'SEIS MIL SETECIENTOS CINCUENTA'
 *                                        → [MONTO_TOTAL_CENTAVOS_LETRAS] 'NOVENTA'
 *
 * A flat list would make the user type "SEIS MIL SETECIENTOS CINCUENTA" by hand.
 * The Pagaré fill guide names the digits-vs-letters mismatch as the most litigated
 * data-entry error in títulos valores, so the derivation is a correctness feature,
 * not a convenience.
 */

import type { EntityIdType, EntityKind } from '../entities/vocabulary'
import {
  ENTITY_ATTRIBUTE_KEYS,
  ENTITY_KINDS,
  isIdentifierProperty,
} from '../vocabulary/crossService'
import { isDerivedFact } from '../entities/derivedFacts'
import { WORKFLOW_INPUT_TYPES } from '../workflowSchemas'

/**
 * Top-level entity slot declared on a template (`entities[]` in `TemplateSchema` /
 * `TemplateDetection` — MAR-1105).
 */
export interface TemplateEntitySlot {
  slot: string
  kind: EntityKind
  label: string
}

/**
 * How a placeholder's value is produced from the inputs.
 *
 * A CLOSED set, deliberately. An open expression language here would be a second
 * programming model living in the database, unreviewable and unversioned — and the
 * transforms it would express are all Salvadoran legal-drafting conventions, which
 * belong in code next to their tests.
 */
export const TEMPLATE_TRANSFORMS = [
  /** Pass the input through unchanged. */
  'identity',
  /** 6750.90 → 'SEIS MIL SETECIENTOS CINCUENTA' (integer part, uppercase words). */
  'amountToWords',
  /** 6750.90 → 'NOVENTA' (cents only, uppercase words). */
  'amountCentsToWords',
  /** 6750.90 → '6,750.90' (digits, two decimals, no currency symbol). */
  'amountToDigits',
  /** A date → its day in lowercase words ('dieciocho'). */
  'dateDayToWords',
  /** A date → its month name in lowercase ('marzo'). */
  'dateMonthToWords',
  /** A date → its year in lowercase words ('dos mil veintiséis'). */
  'dateYearToWords',
  /** '01234567-8' → 'cero uno dos tres cuatro cinco seis siete - ocho'. Identity docs are
   *  always written out in letters, never digits. */
  'idToWords',
  /** Gender flexion driven by a control input: 'salvadoreño' / 'salvadoreña'. */
  'genderFlex',
  /** Title Case Each Word — person names. */
  'titleCase',
  /** ALL CAPS — company names, amounts in the body. */
  'upperCase',
  /** all lowercase — professions, nationalities. */
  'lowerCase',
] as const
export type TemplateTransform = typeof TEMPLATE_TRANSFORMS[number]

export type TemplateInputType = typeof WORKFLOW_INPUT_TYPES[number]

/** Source tier for a field's description (`editada > guía > generada`). */
export type TemplateHelpSource = 'guia' | 'editada' | 'generada'

/** A question the run form asks. Mirrors `workflowInputSchema` so the existing form renders it. */
export interface TemplateInput {
  key: string
  label: string
  type: TemplateInputType
  required: boolean
  description?: string
  placeholder?: string
  /** Options for `select` / `multi_select`. */
  options?: { value: string; label: string }[]
  /** True when this input drives transforms but is NOT itself substituted anywhere —
   *  e.g. `genero_deudor`, which governs flexion and appears nowhere in the document.
   *  No bracket scan will ever find these; they come from the fill guide or the LLM pass. */
  controlOnly?: boolean
  /**
   * 1-to-1 entity property binding (`<slot>.<property>`, e.g. `trabajador.given_names`,
   * `trabajador.national_id`, `inmueble.registry_number` — MAR-1105).
   */
  bind?: `${string}.${string}`
  /**
   * What real-world identifier this input asks for (MAR-129), carried from the detected field's
   * `suggestedIdType`. Projected onto the workflow input's `constraints.entitySource.idType`, which
   * is what the run form matches the active client's entities against.
   */
  idType?: EntityIdType
  /**
   * WHOSE identifier or fact this input asks for — person, organization, property or vehicle
   * (MAR-176 / MAR-1070).
   *
   * Projected onto the workflow input's `constraints.partyKind`, which the run form honours.
   * Omit to allow any kind.
   */
  partyKind?: EntityKind
  /**
   * WHICH ENTITY SLOT of the instrument this input describes (MAR-176 / MAR-1070) — `trabajador`,
   * `patrono`, `otorgante`, `inmueble`, `vehiculo`. Every input sharing a role is one ENTITY BLOCK:
   * picking an entity fills the whole block at once instead of one field at a time.
   *
   * Free text, matching `entitySource.role`, because the roles are the instrument's own vocabulary.
   */
  role?: string
  /**
   * WHICH FACT of that entity fills this input: `name`, `idNumber`, or `attribute:<canonical_key>`.
   * Only meaningful with `role`. Projected onto `entitySource.field`.
   */
  field?: string
}

/**
 * The facts an entity-slot input may bind to: the entity's own `name` / `idNumber`, or one of the
 * canonical attributes across all entity kinds (`ENTITY_ATTRIBUTE_KEYS`). Mirrors
 * `entitySource.field` in `shared/workflowSchemas.ts`.
 *
 * Validated rather than trusted wherever authored data arrives, because an unknown `attribute:<key>`
 * would be an input that silently never fills.
 */
export function isTemplateFact(v: unknown): v is string {
  if (typeof v !== 'string') return false
  const trimmed = v.trim()
  if (!trimmed) return false
  let prop = trimmed
  const dotIdx = trimmed.lastIndexOf('.')
  if (dotIdx !== -1) {
    if (dotIdx === 0 || dotIdx === trimmed.length - 1) return false
    prop = trimmed.slice(dotIdx + 1).trim()
    if (!prop) return false
  }
  // Computed rather than stored — `age` / `derived:age` reads the date of birth a DUI carries and
  // returns the age a comparecencia asks for. See `shared/entities/derivedFacts.ts`.
  if (prop.startsWith('derived:')) return isDerivedFact(prop.slice('derived:'.length))
  if (prop.startsWith('attribute:')) {
    const attr = prop.slice('attribute:'.length)
    return !!attr && (ENTITY_ATTRIBUTE_KEYS as readonly string[]).includes(attr)
  }
  if (prop === 'name' || isIdentifierProperty(prop) || isDerivedFact(prop)) return true
  return (ENTITY_ATTRIBUTE_KEYS as readonly string[]).includes(prop)
}

/** `representante_empleador` → `Representante empleador`, for a derived section heading. */
export function roleLabel(role: string): string {
  const words = role.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** A literal token in the document, and where its value comes from. */
export interface TemplatePlaceholder {
  /** The exact literal as it appears in the .docx, brackets included: '[NOMBRE_DEUDOR]'. */
  token: string
  /** The `TemplateInput.key` supplying the value. */
  inputKey: string
  /** How to render that input's value into this position. */
  transform: TemplateTransform
  /** Positions this token occupies. One capture, N substitutions — never ask twice. */
  occurrences: number
}

/**
 * The check kinds a validation can be.
 *
 * A CLOSED set, for the same reason as `TEMPLATE_TRANSFORMS`: an expression language here
 * would be a second programming model living in the database, unreviewable and unversioned.
 * Anything a guide states that no kind can express becomes an advisory NOTICE instead —
 * visible to the user, never silently dropped.
 */
export const VALIDATION_KINDS = [
  /** `inputKeys[0]` must equal the sum of the rest (times `multipliers`, when given).
   *  Pagaré: total = cuota inicial + nº cuotas × monto de cuota. */
  'sum_equals',
  /** A numeric input must fall within `min`..`max`. Pagaré: día de pago ∈ 1–28, because
   *  29/30/31 produce indeterminate due dates in short months. */
  'range',
  /** Two inputs must be equal. */
  'cross_field_equal',
  /** An input is required when another has a given value. */
  'required_if',
] as const
export type ValidationKind = typeof VALIDATION_KINDS[number]

/** A check run against captured values before the document is generated. */
export interface TemplateValidation {
  /** Stable id so a failure can be traced back to the rule that raised it. */
  id: string
  kind: ValidationKind
  /** Shown to the user when the check fails. */
  message: string
  /** `blocking` refuses generation; `warning` is shown and can be accepted. */
  severity: 'blocking' | 'warning'
  /** Input keys the rule reads, in the order the kind expects. Explicit so the UI can
   *  surface the failure on the right fields rather than as a page-level error. */
  inputKeys: string[]
  /** `range`: bounds. */
  min?: number
  max?: number
  /** `sum_equals`: per-term multipliers, aligned to `inputKeys.slice(1)`. A term whose
   *  multiplier is another input key is multiplied by that input's value. */
  multipliers?: (number | string)[]
  /** `required_if`: the value of `inputKeys[1]` that makes `inputKeys[0]` required. */
  equals?: string
}

/** The approved, stored artifact. Derived from the .docx, never hand-authored.
 *  Identity lives on the `document_templates` row, not in here. */
/**
 * A run-form SECTION (MAR-176) — presentation only.
 *
 * Mirrors `inputGroupSchema` in `shared/workflowSchemas.ts`, and `asWorkflow.ts` projects it
 * straight onto the workflow's `input_groups`. Like that schema it REFERENCES input keys and never
 * nests them: `inputs` stays one flat list, so filling, validation and the placeholder map are
 * untouched by how the form happens to be arranged.
 *
 * Grouping is a real usability concern rather than polish here: the system library runs to 40 inputs
 * on a single template, and one flat wall of 40 fields is what a user has to work through today.
 *
 * A partial grouping is safe by construction — the run form collects every field no group named
 * into a trailing "other" section, so a key missing from `groups` is misplaced, never invisible.
 */
export interface TemplateInputGroup {
  key: string
  label: string
  description?: string
  /** References into `inputs[]`; also defines field order within the section. */
  inputKeys: string[]
}

/**
 * Batch mode configuration (MAR-179) — AUTHORED by a superadmin, one flag per template.
 *
 * Naming the batchable role is the entire configuration: the row shape is then derived as "every
 * input bound to that role", so there is no second declaration of the field set to keep in sync.
 * See `shared/templates/batch.ts`, which owns the model and the derivation.
 *
 * Deliberately not inferred from "does this template have an identity field" — every template does,
 * so that gate would switch batching on for the whole library at once.
 */
export interface TemplateBatchConfig {
  /** The `TemplateInput.role` that repeats. One document per entry. */
  role: string
  /**
   * Row-shape column keys hidden by DEFAULT in the run form's table (MAR-179).
   *
   * A real employment contract's row is 21 columns and needs ~1900px, so something has to be
   * hidden. The table's fallback rule — hide what a matched party fills for itself — is a guess,
   * and every user of the template was re-fixing that guess for themselves, in their own browser.
   * Authoring it once puts the answer where the person who knows the template already is.
   *
   * A DEFAULT, not a restriction: the run form's own «Columnas» menu still overrides it per user,
   * and a hidden column is still filled and still generated. The identity column is never hidden.
   *
   * Absent means "use the fallback rule"; an empty array means "show everything", and the two are
   * deliberately different.
   */
  hiddenColumns?: string[]
}

export interface TemplateSchema {
  /** Top-level entity slots declared on the template (MAR-1105). */
  entities?: TemplateEntitySlot[]
  inputs: TemplateInput[]
  placeholders: TemplatePlaceholder[]
  validations: TemplateValidation[]
  /** Legal warnings shown BEFORE generation, not buried in docs. The Pagaré's instalment
   *  scheme is arguably void under Art. 706 C.Com. — the user has to see that up front. */
  notices: { severity: 'blocking' | 'warning'; message: string }[]
  /** Optional run-form sectioning. Absent on templates imported before MAR-176; the form
   *  renders those as a single section exactly as before. */
  groups?: TemplateInputGroup[]
  /**
   * Batch mode (MAR-179) — which party repeats, so one run produces one document per row.
   *
   * Authored by a superadmin and absent by default: a template with no `batch` runs exactly as it
   * always has. Lives here beside `groups`, `notices` and `validations` — the authored half of the
   * schema, the part that cannot be re-derived from the `.docx` and therefore travels in a bundle.
   * See `shared/templates/batch.ts` for the model.
   */
  batch?: TemplateBatchConfig
}

// ─── Detection (module 1 output — a proposal awaiting human confirmation) ────────

/** Which pass found a candidate. `both` is the strongest signal. */
export type TemplateFieldSource = 'regex' | 'llm' | 'both'

/**
 * One row of a Guidelines document's placeholder table.
 *
 * These are AUTHORED facts, parsed structurally from the Word table (`meta.table` gives
 * `{table,row,cell}`), with no model in the loop — so nothing here can be hallucinated.
 * They outrank both the model's opinion and our naming heuristics.
 */
export interface GuidelineField {
  key: string
  /** The placeholder name exactly as the guide writes it, e.g. `NOMBRE_PERSONA_CONTRATANTE`. */
  name: string
  /** `Descripción` column — what the field is. */
  description: string
  /** `Formato` column, verbatim. Kept even when unmapped, so a reviewer sees what the
   *  guide actually said rather than a silent fallback. */
  format: string
  /** `Notas` column — cross-references, warnings, "appears twice", and so on. */
  notes: string
  /** The transform `format` maps to, when it maps to one at all. */
  transform?: TemplateTransform
  /** True when `format` was a broad phrase like "en letras" that covers several transforms.
   *  An ambiguous mapping does NOT override a more specific naming-heuristic answer. */
  transformAmbiguous?: boolean
}

/**
 * A per-window verification verdict on one candidate.
 *
 * `reject` NEVER removes a candidate — it pre-unchecks the row and shows `reason`.
 * A bracketed token is evidence: it is physically in the document, and a model calling
 * it a false positive is an opinion. Letting that opinion delete a field would drop real
 * fields silently, which is the one failure a reviewer could never catch by looking.
 */
export interface TemplateFieldVerdict {
  status: 'keep' | 'reject' | 'retype'
  reason: string
  /** Present on `retype` — what the verifier thinks the field should be instead. */
  suggestedType?: TemplateInputType
  suggestedTransform?: TemplateTransform
}

export interface TemplateFieldCandidate {
  /** The literal text to replace. A bracketed token from the regex pass, or an
   *  in-place literal ('Juan Carlos Martínez Rivas') from the LLM pass. */
  token: string
  /** Proposed schema key, snake_case. */
  key: string
  label: string
  source: TemplateFieldSource
  occurrences: number
  /** Paragraph indexes (`Segment.index`) the token appears in. */
  segmentIndexes: number[]
  suggestedType: TemplateInputType
  suggestedTransform: TemplateTransform
  /**
   * 1-to-1 entity property binding (`<slot>.<property>`, e.g. `trabajador.given_names` — MAR-1105).
   */
  suggestedBind?: `${string}.${string}`
  /**
   * **What real-world thing this placeholder asks for** (MAR-129) — the semantic axis, carried
   * alongside the widget type so the run form can offer the active client's or case's matching
   * identifiers. `[DUI DEL ARRENDATARIO]` is `suggestedType: 'text'` + `suggestedIdType:
   * 'national_id'`. Detection already recognised DUI and NIT keys to pick a transform and then
   * threw the recognition away; this keeps it. Undefined = no semantic meaning detected.
   */
  suggestedIdType?: EntityIdType
  /** Whose identifier the field asks for — see `TemplateInput.partyKind`. Detection infers it
   *  alongside `suggestedIdType`; the reviewer's correction survives a re-scan like any other. */
  suggestedPartyKind?: EntityKind
  /** The party this field describes — see `TemplateInput.role`. */
  suggestedRole?: string
  /** The fact of that party it holds — see `TemplateInput.field`. */
  suggestedFact?: string
  /**
   * Set when the model's role assignment DISAGREED with what the field's own naming implies.
   *
   * The field is left unassigned in that case rather than assigned on the model's word: a wrong role
   * fills the wrong person's name and identity number into a legal instrument, which is a different
   * class of error from a misplaced form section. Surfaced so a reviewer can settle it, the way
   * `verdict` already surfaces a rejected candidate instead of dropping it.
   */
  roleConflict?: { model: string; naming: string }
  /** LLM pass only — why this looks templatable. Shown in the confirmation UI so a
   *  reviewer can judge a suggestion instead of trusting it. */
  rationale?: string
  /** LLM pass only. Regex hits are certain by construction and carry none. */
  confidence?: 'high' | 'medium' | 'low'
  /** Index of the page-sized window this field was verified in. A `.docx` carries no
   *  page numbers, so windows (`binSegments`) are the addressable stand-in. */
  windowIndex?: number
  /** Verification result. Absent when the verify pass did not run or failed. */
  verdict?: TemplateFieldVerdict
  /**
   * Guidance carried over from the Guidelines document's `Notas` column — cross-references,
   * "appears twice in the document", pointers to pending legal questions. Free text, shown
   * to the reviewer and stored with the field; it is the authored context that neither the
   * bracket scan nor the model can produce.
   */
  notes?: string
  /**
   * The Guidelines document's `Descripción` column, an author edit, or a detection-generated hint —
   * what this field IS, for the lawyer filling the run form.
   */
  help?: string
  /** Source tier of `help`, ordered `editada > guía > generada` (MAR-1070). */
  helpSource?: TemplateHelpSource
  /** True when a Guidelines document supplied this field's label/format/notes. Such a field
   *  is asserted by a human, not inferred, and the UI marks it accordingly. */
  fromGuideline?: boolean
  /** The `Formato` string verbatim when the guide gave one we could not map to a transform.
   *  Surfaced rather than swallowed, so an unmapped format is visible, not silent. */
  unmappedFormat?: string
  /** Options for `select` / `multi_select` fields authored or inferred in the draft. */
  suggestedOptions?: { value: string; label: string }[]
}

export {
  convertPartySelectCandidates,
  convertPartySelectInputs,
  normalizeFactProperty,
  normalizeTemplateSchema,
  parseEntityBind,
  sanitizeEntitySlots,
} from './normalizeSchema'

export interface TemplateDetection {
  /** Top-level entity slots proposed by the party-block pass (MAR-1105). */
  entities?: TemplateEntitySlot[]
  /** Run-form sectioning proposed by the party-block pass — party blocks first, then the topical
   *  grouping of whatever belongs to no party. Carried here so the save path never re-asks. */
  groups?: TemplateInputGroup[]
  candidates: TemplateFieldCandidate[]
  /** Paragraphs scanned. */
  segmentCount: number
  regexCount: number
  llmCount: number
  /** Set when the LLM pass failed; the regex candidates are still returned.
   *  Detection degrades, it does not fail — a reviewer can work from brackets alone. */
  llmError?: string
  /** Page-sized windows the document was split into for verification. */
  windowCount?: number
  /** Windows whose verification call failed. Their candidates come back unverified
   *  rather than dropped — partial verification is still worth more than none. */
  windowsFailed?: number
  /** Legal warnings extracted from the guide's prose (plan step 8). Shown to the reviewer,
   *  editable, and carried into `TemplateSchema.notices` on save. */
  notices?: { severity: 'blocking' | 'warning'; message: string }[]
  /** Set when a Guidelines document was supplied and parsed. */
  guideline?: {
    fieldCount: number
    /** Placeholders the guide documents that the template does NOT contain — the two have
     *  drifted, or the guide is aspirational. */
    missingFromTemplate: string[]
    /** Tokens in the template the guide does not document — undocumented fields. */
    undocumented: string[]
  }
}

/** Result of checking a candidate template against the system template it overrides. */
export interface TemplateCompatibility {
  ok: boolean
  /** Inputs the system template needs that the candidate has no placeholder for.
   *  This is what blocks an override. */
  missing: string[]
  /** Tokens the candidate carries that the system template does not know about.
   *  Allowed — they become additional inputs — but surfaced so the user can see them. */
  extra: string[]
}

/**
 * Compare an override candidate against the system template.
 *
 * Matching is by input KEY against the SYSTEM template, always — the system template
 * is authoritative, and an org's file is measured against it, never the reverse.
 */
export function checkTemplateCompatibility(
  system: TemplateSchema,
  candidateTokens: string[],
): TemplateCompatibility {
  const present = new Set(candidateTokens)
  const missing: string[] = []
  for (const input of system.inputs) {
    if (input.controlOnly) continue // never appears in the document; nothing to match
    const covered = system.placeholders
      .filter(p => p.inputKey === input.key)
      .some(p => present.has(p.token))
    if (!covered) missing.push(input.key)
  }
  const known = new Set(system.placeholders.map(p => p.token))
  const extra = candidateTokens.filter(t => !known.has(t))
  return { ok: missing.length === 0, missing, extra }
}

/** A missing field, in the terms the person fixing the document actually sees. */
export interface MissingField {
  /** The input key — stable, for the client to key off. */
  key: string
  /** The label the run form shows for it. */
  label: string
  /** The exact tokens that would satisfy it. Any ONE of them is enough. */
  tokens: string[]
}

/**
 * Resolve missing input keys to labels and the tokens that would fix them (MAR-163).
 *
 * `checkTemplateCompatibility` reports keys because keys are what it matches on. A key is
 * the wrong thing to show a lawyer: `monto_total_letras` names an implementation detail,
 * and the person who has to repair the `.docx` needs the LABEL to know what is meant and
 * the TOKEN to know what to type.
 */
export function missingFields(system: TemplateSchema, missing: string[]): MissingField[] {
  return missing.map((key) => {
    const input = system.inputs.find(i => i.key === key)
    return {
      key,
      label: input?.label ?? key,
      tokens: system.placeholders.filter(p => p.inputKey === key).map(p => p.token),
    }
  })
}

/**
 * The refusal message, in plain Spanish and in the user's terms — no keys (MAR-163).
 *
 * The audience is a firm's admin who uploaded their letterhead version and needs to know
 * precisely what to add before trying again.
 */
export function describeMissing(system: TemplateSchema, missing: string[]): string {
  const fields = missingFields(system, missing)
  const detail = fields
    .map(f => (f.tokens.length ? `${f.label} (${f.tokens[0]})` : f.label))
    .join(', ')
  const count = fields.length
  return count === 1
    ? `Tu documento no puede sustituir a la plantilla de Marcus: le falta un campo obligatorio — ${detail}. `
      + 'Agrégalo al documento y vuelve a subirlo.'
    : `Tu documento no puede sustituir a la plantilla de Marcus: le faltan ${count} campos obligatorios — ${detail}. `
      + 'Agrégalos al documento y vuelve a subirlo.'
}
